import {dataClient} from '../data/dataClient.js';
import {assembleContext} from './context.js';
import {assertAllowance,consumeOperations} from './aoMeter.js';
import {requireSafeContent,runScopeCheck,scopeDeclineMessage,SCOPE_DECLINE_CODE} from './guardrails.js';
import {withModelRetry,getInjectedFault,getDevBackoffOverrides,makeFaultError} from './modelRetry.js';
import {getCachedResponse,putCachedResponse,purgeExpiredCacheEntries} from './responseCache.js';
import {withBusinessConcurrency} from './concurrency.js';
import {withTierFallback} from './tierFallback.js';
import {isDirectOpenAiCompatible,resolveProviderBaseUrl,callOpenAiCompatible} from './providers.js';

const pause=(ms)=> new Promise((resolve)=> setTimeout(resolve,ms));

// BB-3: opportunistic purge of expired response-cache rows on module load.
purgeExpiredCacheEntries();

// Platform-tier is a DEV/TESTING-ONLY path (owner decision 2026-09-08): there
// is no platform-hosted model tier for real tenants. In a production bundle
// (Vite build) a bot without a BYO credential must surface a clear "connect
// your own API key" state — never mock text, never a silent platform call.
// In dev (`npm run dev`) the canned mock stays available for UI/QA work but is
// always labelled as a mock so it can't be mistaken for a live reply.
const IS_PRODUCTION_BUILD = import.meta.env?.PROD === true;
const ALLOW_MOCK = import.meta.env?.DEV === true || import.meta.env?.MODE === 'test' || process.env?.NODE_ENV === 'test';

function botModelSource(bot) {
  return bot?.modelSource === 'byo' ? 'byo' : 'platform';
}

function getBot(state,botId) {
  const bot=state.bots.find((item)=> item.id===botId);

  if (!bot || !bot.enabled) {
    throw new Error('The selected bot is unavailable.');
  }

  return bot;
}

function getSelectedCredential(state,bot) {
  if (botModelSource(bot)!=='byo') return null;

  const credential=(state.providerCredentials || []).find(
    (item)=> item.id===bot.byoModelId && item.enabled
  );

  if (!credential) {
    const error = new Error(
      `"${bot.name}" is set to use a provider that is not connected or enabled. Connect your own API key (Models → Connected LLM providers) and assign it to this bot (Bots → model source).`
    );
    error.code = 'NO_PROVIDER_CONNECTED';
    throw error;
  }

  return credential;
}

function mockModelResponse(bot,prompt,depth=0,credential=null) {
  const source=credential
    ? `${credential.label} using ${credential.model}`
    : 'the platform model tier';

  if (depth > 0 && bot.isLead) {
    return `${bot.name} reviewed the request without delegating further because consultation depth is capped at one.`;
  }

  if (bot.role.includes('Revenue')) {
    return `(${source}) Revenue concentration is the main sensitivity; repeat-customer demand remains the strongest driver.`;
  }

  if (bot.role.includes('Cash')) {
    return `(${source}) Moving the supplier payment by two weeks preserves approximately six additional weeks of operating buffer.`;
  }

  if (bot.role.includes('Copy')) {
    return `(${source}) Lead with the tangible customer outcome, then prove it with material traceability rather than broad sustainability claims.`;
  }

  return `(${source}) ${bot.name} recommends clarifying the desired outcome, evidence, owner, and approval gate before execution.`;
}

// BB-0 (docs/build_bot_platform.md §3.2.1): the client pass-through runs under
// the retry/timeout/backoff contract (30s timeout, 3 retries, 1s/2s/4s+jitter —
// src/services/modelRetry.js), mirroring the server modelRouter.chat contract
// (docs/backend.md §7.4). Consult sub-calls inherit the same policy via
// `faultScope: 'consult'`. No content is stored server-side.
//
// BB-3 additions: identical re-sends consult the client-only response cache
// first (a hit performs no provider call and consumes no AO); the response is
// cached after a successful call.
//
// Fix #2 (docs/deploy_fixes.md): when a BYO credential is present the call is a
// REAL OpenAI-compatible provider POST (src/services/providers.js). Platform-
// tier bots (no credential) throw NO_PROVIDER_CONNECTED in production — a clear
// "connect your own API key" state, never canned text, never a silent fallback.
async function callModelPassThrough(bot,sessionId,prompt,depth,credential,faultScope='model') {
  const context=await assembleContext(bot.id,sessionId,prompt);
  const byo = botModelSource(bot) === 'byo' && credential;

  // Cache key: model identity + the calling bot. Two specialists on the same
  // platform tier are DIFFERENT model contexts (different system prompts) and
  // must never share a cached reply; the bot id scopes the key correctly.
  const modelKey=credential
    ? `${credential.provider}/${credential.model}`
    : `platform/${bot.modelTier}/${bot.id}`;

  const cacheLookup=await getCachedResponse({modelKey,messages: [prompt,depth]});

  if (cacheLookup.hit) {
    return {
      text: cacheLookup.text,
      context,
      fromCache: true,
      provider: credential
        ? {
            id: credential.id,
            provider: credential.provider,
            model: credential.model,
            endpoint: resolveProviderBaseUrl(credential) || credential.endpoint || null
          }
        : {source: 'platform'}
    };
  }

  const text=await withModelRetry(async (attempt)=> {
    const fault=getInjectedFault(faultScope,attempt);
    if (fault) throw makeFaultError(fault);

    if (byo) {
      // REAL provider call (fix #2). Anthropic and other non-OpenAI-compatible
      // providers get a clear error here — never a wrong-format call.
      if (!isDirectOpenAiCompatible(credential)) {
        const error = new Error(
          `"${credential.label}" (${credential.provider}) is not OpenAI-compatible for direct calls. Re-save it as a Custom provider with an OpenAI-compatible endpoint, or pick a supported provider.`
        );
        error.code = 'PROVIDER_NOT_OPENAI_COMPAT';
        throw error;
      }
      return (await callOpenAiCompatible({
        credential,
        messages: buildProviderMessages(bot, context, prompt)
      })).text;
    }

    if (IS_PRODUCTION_BUILD || !ALLOW_MOCK) {
      const error = new Error(
        `"${bot.name}" has no connected provider. Connect your own API key (Models → Connected LLM providers) and assign it in Bots — the platform model tier is not available to tenants.`
      );
      error.code = 'NO_PROVIDER_CONNECTED';
      throw error;
    }

    // Dev-only canned mock — labelled so it can't pass as a live reply.
    await pause(450);
    return `[dev mock] ${mockModelResponse(bot,prompt,depth,credential)}`;
  },getDevBackoffOverrides());

  await putCachedResponse({modelKey,messages: [prompt,depth],text});

  return {
    text,
    context,
    provider: credential
      ? {
          id: credential.id,
          provider: credential.provider,
          model: credential.model,
          endpoint: resolveProviderBaseUrl(credential) || credential.endpoint || null
        }
      : {source: 'platform'}
  };
}

function buildProviderMessages(bot, context, prompt) {
  // Messages for the REAL provider call (fix #2): system prompt carries the
  // bot identity + system prompt + brand voice, plus scoped memory/plans as
  // compact context (build.md §17.2 client-side assembly order). Recent chat
  // lines are included as plain turns up to the context cap already applied
  // in context.js. Content is client-local — never persisted server-side.
  const brand = context.brandProfile || {};
  const brandBlock = [
    brand.voice ? `Brand voice: ${brand.voice}` : null,
    brand.avoid ? `Avoid: ${brand.avoid}` : null,
    Array.isArray(brand.principles) && brand.principles.length
      ? `Principles: ${brand.principles.join('; ')}`
      : null
  ].filter(Boolean).join('\n');

  const memoryBlock = Array.isArray(context.businessMemory) && context.businessMemory.length
    ? context.businessMemory.slice(0, 6).map((m) => `- ${m.content || m.text || JSON.stringify(m)}`).join('\n')
    : '';

  const filesBlock = Array.isArray(context.files) && context.files.length
    ? context.files.slice(0, 5).map((f) => `- ${f.name} (${f.size || 'unknown'})`).join('\n')
    : '';

  const system = [
    `You are ${bot.name}, ${bot.role} in a business command center.`,
    ...(bot.systemPrompt ? [bot.systemPrompt] : []),
    ...(brandBlock ? [`\nBusiness context:\n${brandBlock}`] : []),
    ...(memoryBlock ? [`\nKnown facts (business memory):\n${memoryBlock}`] : []),
    ...(filesBlock ? [`\nAttached reference files:\n${filesBlock}`] : [])
  ].join('\n');

  const messages = [{ role: 'system', content: system }];
  if (context.recentMessages) {
    const history = String(context.recentMessages).split('\n').filter(Boolean).slice(-8);
    for (const line of history) messages.push({ role: 'user', content: line });
  }
  messages.push({ role: 'user', content: prompt });
  return messages;
}

export async function sendChatMessage(sessionId,message) {
  const state=dataClient.peek();
  const businessId=state.business?.id || 'default';

  // BB-3 (docs/build_bot_platform.md §3.2.3): the whole turn runs under the
  // per-business concurrency cap. Over-cap sends queue FIFO here; the server
  // mirror rejects saturated sends with 429 AO_CONCURRENCY_LIMIT.
  return withBusinessConcurrency(businessId,async ()=> {
    return runChatTurn(sessionId,message);
  });
}

async function runChatTurn(sessionId,message) {
  const state=dataClient.peek();
  const session=state.chatSessions?.find((item)=> item.id===sessionId);
  const botId=session?.botId || message.botId;
  const bot=getBot(state,botId);
  const prompt=typeof message==='string' ? message : message.text;
  const sandbox=Boolean(message.sandbox);

  // frontend.md §9 send pipeline order: guardrails check FIRST (outbound
  // prompt), then context assembly + AO pre-flight, then the model call.
  // Coded so the UI can render the policy message verbatim (§10).
  requireSafeContent(prompt, 'CONTENT_POLICY_BLOCKED');

  // Scope guardrail (T3 — cross-screen-overview-and-live-share.md §2.8): a
  // SECOND, distinct check from content safety. runScopeCheck is deterministic
  // keyword overlap vs the bot's declared scope and runs BEFORE credential
  // resolution or any model call — so an out-of-scope ask declines cleanly even
  // with no provider key configured, never consults teammates, and costs ZERO
  // AO (no consumeOperations, no usage_log with ops>0). The exchange persists
  // like a normal turn unless the caller is a sandbox test chat.
  const scopeCheck=runScopeCheck(bot,prompt);

  if (!scopeCheck.inScope) {
    const response={
      id:`msg_${Date.now()}`,
      sender:'bot',
      botId:bot.id,
      text:scopeDeclineMessage(bot),
      declinedScope:true,
      scopeDeclineCode:SCOPE_DECLINE_CODE,
      consultedBots:[],
      degradedConsults:[],
      fromCache:false,
      tierUsed:bot.modelTier,
      providerCredentialId:null,
      module:session?.module || message.module || 'headquarters',
      sessionId,
      created:new Date().toISOString()
    };
    if (!sandbox) await dataClient.addMessage(response.module,response);
    return response;
  }

  const credential=getSelectedCredential(state,bot);

  const consultableBots=(bot.consultableBots || [])
    .map((id)=> state.bots.find((item)=> item.id===id && item.enabled))
    .filter(Boolean);
  const participants=[bot,...(bot.isLead ? consultableBots : [])];
  const operationEstimate=participants.length;

  // AO is asserted BEFORE any model call (aoMeter.js mirror; server-authoritative
  // once built). The estimate is the worst case; actual consumption below may be
  // lower (cache hits and degraded consults are never billed).
  assertAllowance(operationEstimate,state);

  const consultedBots=[];
  const specialistReplies=[];
  const degradedConsults=[];

  for (const specialist of bot.isLead ? consultableBots : []) {
    // Lead-consult UX (frontend.md §9): show a legible intermediate state —
    // "Consulting <teammate>…" — never a bare spinner. The UI callback is
    // optional so programmatic callers (flows/tests) stay quiet.
    message.onConsulting?.(specialist);

    const specialistCredential=getSelectedCredential(state,specialist);

    try {
      // BB-0 (docs/build_bot_platform.md §4): consult sub-calls inherit the
      // retry/timeout/backoff policy (faultScope 'consult'). A consult that
      // still fails after retry exhaustion DEGRADES — it never fails the turn.
      const result=await callModelPassThrough(
        specialist,
        sessionId,
        prompt,
        1,
        specialistCredential,
        'consult'
      );

      requireSafeContent(result.text);

      // Sandbox test chats never persist consult logs (T3) — logId is null.
      const log=sandbox ? null : await dataClient.create('botConsultationLogs',{
        sessionId,
        leadBotId: bot.id,
        consultedBotId: specialist.id,
        providerCredentialId: specialistCredential?.id || null,
        depth: 1,
        created: new Date().toISOString()
      });

      consultedBots.push({
        id: specialist.id,
        name: specialist.name,
        role: specialist.role,
        logId: log?.id || null,
        fromCache: Boolean(result.fromCache)
      });
      specialistReplies.push(result.text);
    } catch (consultError) {
      if (consultError.code==='CONTENT_POLICY_BLOCKED') throw consultError;

      // Deterministic policy blocks on a specialist reply are surfaced as a
      // normal turn failure upstream; transient/model errors degrade instead.
      degradedConsults.push({
        id: specialist.id,
        name: specialist.name,
        role: specialist.role,
        code: consultError.code || 'MODEL_UNAVAILABLE'
      });
    }
  }

  await pause(300);

  let synthesized;
  let leadFromCache=false;
  let leadTierUsed=bot.modelTier;
  let leadDegraded=false;

  if (specialistReplies.length) {
    const consultedSummary=consultedBots.map((item)=> item.name).join(' and ');
    const degradedSummary=degradedConsults
      .map((item)=> `${item.name} was unavailable (${item.code})`)
      .join('; ');

    synthesized=`I consulted ${consultedSummary}. ${specialistReplies.join(' ')}${degradedConsults.length ? ` (Consultation unavailable: ${degradedSummary}.)` : ''}`;
  } else {
    // The Lead's own model call — BB-3 (docs/build_bot_platform.md §3.2.2):
    // retry contract + graceful tier degradation (deep → balanced → fast) on
    // transient provider failure. Policy/4xx errors never degrade. If every
    // tier fails the turn fails with the classified error (no answer exists).
    const leadTurn=await withTierFallback(
      (tierId)=> callModelPassThrough(
        {...bot,modelTier: tierId},
        sessionId,
        prompt,
        0,
        credential
      ),
      bot.modelTier
    );

    leadFromCache=Boolean(leadTurn.result.fromCache);
    leadTierUsed=leadTurn.tierUsed;
    leadDegraded=leadTurn.degraded;
    synthesized=leadTurn.result.text;

    if (degradedConsults.length) {
      const degradedSummary=degradedConsults
        .map((item)=> `${item.name} was unavailable (${item.code})`)
        .join('; ');

      synthesized=`I could not complete the planned consultations, so this is my own best answer. (${degradedSummary}.) ${synthesized}`;
    }
  }

  const text=requireSafeContent(synthesized);

  // Billing fidelity (BB-0/BB-3): only model calls that actually hit a
  // provider consume an operation. Degraded consults and cache hits are free.
  // When specialists replied, the Lead turn is the synthesis of their output:
  // if every reply came from cache, the whole turn is a cache hit — 0 AO.
  const billedConsults=consultedBots.filter((item)=> !item.fromCache);
  const billedLead=specialistReplies.length
    ? billedConsults.length > 0
    : !leadFromCache;

  if (billedLead) {
    await consumeOperations({
      operations: 1,
      mode: 'agentic',
      botId: bot.id,
      action: leadDegraded
        ? `Lead answered chat via ${credential?.provider || 'platform'} (degraded to ${leadTierUsed})`
        : `Lead answered chat via ${credential?.provider || 'platform'}`
    });
  }

  for (const participant of billedConsults) {
    await consumeOperations({
      operations: 1,
      mode: 'agentic',
      botId: participant.id,
      action: 'Specialist consulted during chat'
    });
  }

  const response={
    id: `msg_${Date.now()}`,
    sender: 'bot',
    botId: bot.id,
    text,
    consultedBots,
    degradedConsults,
    fromCache: leadFromCache || consultedBots.some((item)=> item.fromCache),
    tierUsed: leadTierUsed,
    providerCredentialId: credential?.id || null,
    module: session?.module || message.module || 'headquarters',
    sessionId,
    created: new Date().toISOString()
  };

  if (!sandbox) await dataClient.addMessage(response.module,response);
  return response;
}

export async function sendChat({module,botId,text,onConsulting,sandbox=false}) {
  const sessionId=`session_${module}`;
  const existing=dataClient.peek().chatSessions?.find(
    (item)=> item.id===sessionId
  );

  if (!existing) {
    await dataClient.create('chatSessions',{
      id: sessionId,
      module,
      botId,
      title: `${module} workspace conversation`,
      status: 'active'
    });
  }

  return sendChatMessage(sessionId,{module,botId,text,onConsulting,sandbox});
}

export async function approvePlan(planId) {
  const state=dataClient.peek();
  const plan=state.plans.find((item)=> item.id===planId);

  if (!plan) throw new Error('Plan not found.');

  const safeContent=requireSafeContent(JSON.stringify(plan.content || {}));
  const next=await dataClient.create('plans',{
    ...plan,
    id: undefined,
    status: 'approved',
    version: plan.version + 1,
    sourcePlanId: plan.id,
    content: JSON.parse(safeContent)
  });

  if (plan.outputContract==='site_block') {
    await dataClient.create('siteBlocks',{
      planId: next.id,
      status: 'draft',
      approvalRequired: true,
      content: next.content
    });
  } else {
    await dataClient.create('tasks',{
      title: `Execute ${next.title}`,
      status: 'todo',
      ownerType: 'bot',
      owner: plan.botId,
      planId: next.id,
      due: new Date(Date.now() + 7 * 86400000).toISOString().slice(0,10)
    });
  }

  return next;
}

export async function versionPlan(planId) {
  const state=dataClient.peek();
  const plan=state.plans.find((item)=> item.id===planId);

  if (!plan) throw new Error('Plan not found.');

  return dataClient.create('plans',{
    ...plan,
    id: undefined,
    status: 'draft',
    version: plan.version + 1,
    sourcePlanId: plan.id
  });
}

export async function runAgentFlow(flowId,input) {
  const state=dataClient.peek();
  const flow=state.agentFlows.find((item)=> item.id===flowId);

  if (!flow) throw new Error('Agent flow not found.');

  assertAllowance(flow.steps.length,state);

  const results=[];

  for (const step of flow.steps.sort((a,b)=> a.position - b.position)) {
    if (step.stepType==='deterministic') {
      results.push({
        step: step.position,
        output: step.config?.value || input
      });
      continue;
    }

    if (step.stepType==='tool_call') {
      results.push({
        step: step.position,
        output: `Mock tool result for ${step.config?.tool || 'configured tool'}`
      });
      continue;
    }

    const bot=getBot(state,flow.bot);
    const credential=getSelectedCredential(state,bot);
    const result=await callModelPassThrough(
      bot,
      `flow_${flowId}`,
      input,
      0,
      credential
    );

    results.push({
      step: step.position,
      output: requireSafeContent(result.text)
    });
  }

  await consumeOperations({
    operations: flow.steps.length,
    mode: 'workflow',
    botId: flow.bot,
    action: 'Agent flow completed'
  });

  return {flowId,results};
}

export async function installBotTemplate(templateId) {
  // KNOWN DEFERRAL (docs/build-tasks-and-progress.json wave_plan): marketplace
  // install materializes no bot records today — dataClient.installTemplate only
  // records the template id as installed. knowledge_scope/capabilities must be
  // carried where bot records actually materialize (seed / Agent Builder /
  // future PB write-through), not here.
  await dataClient.installTemplate(templateId);
  return {installed: true,templateId};
}

export async function installTeamTemplate(teamTemplateId) {
  // Same deferral as installBotTemplate — id-only until install materializes
  // bot records.
  await dataClient.installTemplate(teamTemplateId);
  return {installed: true,teamTemplateId};
}

// --- BB-1: multi-bot CRUD (docs/build_bot_platform.md §4 Phase BB-1) ---
// All writes go through dataClient (the data-access boundary). The one-Lead
// invariant (build.md §7.2: a multi-bot team must have exactly one Lead at
// save time) is enforced client-side here; the server mirrors it once built.

const BOT_COLORS=['#111827','#5b8def','#62c5a7','#ef806e','#9979dc','#f3c969'];

function slugifyName(name) {
  return String(name || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g,'-')
    .replace(/^-+|-+$/g,'')
    .slice(0,48) || 'bot';
}

function uniqueSlug(state,slug) {
  const taken=new Set(
    (state.bots || []).map((item)=> item.slug || item.id)
  );
  let next=slug;
  let suffix=2;

  while (taken.has(next)) {
    next=`${slug}-${suffix++}`;
  }

  return next;
}

export async function createBot({
  name,
  role='',
  systemPrompt='',
  modelTier='tier_balanced',
  teamId=null,
  isLead=false,
  consultableBots=[],
  agentClass='chat_simple',
  tools=[],
  capabilities=[],
  knowledgeScope='',
  enabled=true
}) {
  if (!String(name || '').trim()) {
    throw new Error('A bot name is required.');
  }

  const state=dataClient.peek();
  const safePrompt=requireSafeContent(systemPrompt);
  const team=(state.teams || []).find((item)=> item.id===teamId);

  // One-Lead rule (build.md §7.2): if this new bot is a Lead joining a
  // team that already has a Lead, reject at save time.
  if (isLead && team) {
    const existingLead=(state.bots || []).find(
      (item)=> item.teamId===team.id && item.isLead
    );

    if (existingLead) {
      const error=new Error(
        `"${team.name}" already has a Lead (${existingLead.name}). A team can have exactly one Lead — demote ${existingLead.name} first.`
      );
      error.code='TEAM_ALREADY_HAS_LEAD';
      throw error;
    }
  }

  const bot=await dataClient.create('bots',{
    name: name.trim(),
    role,
    slug: uniqueSlug(state,slugifyName(name)),
    systemPrompt: safePrompt,
    modelTier,
    teamId: team?.id || null,
    modelSource: 'platform',
    byoModelId: null,
    agentClass,
    tools,
    capabilities,
    knowledgeScope,
    enabled,
    isLead,
    consultableBots,
    color: BOT_COLORS[(state.bots || []).length % BOT_COLORS.length],
    business: state.business?.id || null
  });

  return bot;
}

export async function cloneBot(botId) {
  const state=dataClient.peek();
  const source=state.bots.find((item)=> item.id===botId);

  if (!source) throw new Error('Bot not found.');

  // A clone is a lone specialist by default (never auto-Lead) — the user
  // promotes and wires consults explicitly afterwards.
  return createBot({
    name: `${source.name} (copy)`,
    role: source.role,
    systemPrompt: source.systemPrompt,
    modelTier: source.modelTier,
    teamId: null,
    isLead: false,
    consultableBots: [],
    agentClass: source.agentClass,
    tools: [...(source.tools || [])],
    capabilities: [...(source.capabilities || [])],
    knowledgeScope: source.knowledgeScope || '',
    enabled: source.enabled
  });
}

export async function createCustomTeam({
  name,
  module='headquarters',
  leadName,
  leadRole='',
  leadSystemPrompt='',
  leadModelTier='tier_deep',
  members=[]
}) {
  if (!String(name || '').trim()) {
    throw new Error('A team name is required.');
  }
  if (!String(leadName || '').trim()) {
    throw new Error('The team Lead needs a name.');
  }

  const state=dataClient.peek();
  const teammates=members.filter(
    (member)=> member.name?.trim() && member.name!==leadName
  );

  const seen=new Set();
  const dupes=teammates.filter((member)=> {
    if (seen.has(member.name)) return true;
    seen.add(member.name);
    return false;
  });

  if (dupes.length) {
    throw new Error(
      `Duplicate teammate names are not allowed: ${dupes.map((item)=> item.name).join(', ')}.`
    );
  }

  const modules=state.modules || [];
  // Seeded teams carry the module SLUG (mockData teams use module: 'finance';
  // ChatModule matches `team.module === module` by slug) — keep custom teams
  // consistent so module screens find them.
  const validModule=modules.some((item)=> item.slug===module)
    ? module
    : 'headquarters';

  const team=await dataClient.create('teams',{
    name: name.trim(),
    slug: uniqueSlug(state,slugifyName(name)),
    module: validModule,
    enabled: true,
    custom: true,
    business: state.business?.id || null
  });

  const specialists=[];

  for (const member of teammates) {
    specialists.push(await createBot({
      name: member.name,
      role: member.role || `${name.trim()} specialist`,
      systemPrompt: member.systemPrompt || `Specialist for ${name.trim()} within ${state.business?.name || 'the business'}.`,
      modelTier: member.modelTier || 'tier_balanced',
      teamId: team.id,
      isLead: false,
      consultableBots: []
    }));
  }

  const lead=await createBot({
    name: leadName,
    role: leadRole || `${name.trim()} Lead`,
    systemPrompt: leadSystemPrompt || `Lead the ${name.trim()} team: consult your specialists, then synthesize one answer.`,
    modelTier: leadModelTier,
    teamId: team.id,
    isLead: true,
    consultableBots: specialists.map((item)=> item.id),
    agentClass: 'tooling_advanced'
  });

  return {team,lead,specialists};
}

export async function searchMemory(query) {
  const state=dataClient.peek();
  const records=[
    ...Object.values(state.messages || {}).flat(),
    ...(state.businessMemory || []),
    ...(state.plans || [])
  ];

  return records.filter((record)=> String(query)
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((term)=> JSON.stringify(record).toLowerCase().includes(term)));
}

export async function promoteToMemory(chatMessageId) {
  const state=dataClient.peek();
  const message=Object.values(state.messages || {})
    .flat()
    .find((item)=> item.id===chatMessageId);

  if (!message) throw new Error('Chat message not found.');

  return dataClient.create('businessMemory',{
    sourceMessageId: chatMessageId,
    module: message.module,
    content: requireSafeContent(message.text),
    tags: ['manual-pin'],
    extractionMethod: 'manual_pin'
  });
}

export async function ingestKnowledgeSource(payload) {
  const source=await dataClient.create('knowledgeSources',{
    ...payload,
    ingestedStatus: 'queued'
  });

  // BB-2 (docs/build_bot_platform.md §4): ingest is client-side until the node
  // server lands (its /api endpoints are the Phase-3 target state). The queue
  // → complete lifecycle is real; the processing itself is a local no-op mock
  // that the server-mediated ingest will replace behind this same boundary.
  return dataClient.update(
    'knowledgeSources',
    source.id,
    {ingestedStatus: 'mock_complete'}
  );
}

// --- BB-2: real Agent Builder (docs/build_bot_platform.md §4 Phase BB-2) ---
// Natural-language requirement → DRAFTED bot manifest → human review/edit →
// install. The approval gate mirrors the site_block pattern (build.md §11):
// nothing installs until the owner approves the draft. Drafting is
// deterministic heuristics in the mock client; the agent-bot-builder platform
// bot (bots/platform/agent-bot-builder.json, tooling_advanced + is_lead) owns
// this flow once server-mediated chat exists.

const CAPABILITY_RULES=[
  {capability: 'document_analysis',patterns: [/\bdocument/i,/\binvoice/i,/\bcontract/i,/\breport/i,/\bfile/i]},
  {capability: 'web_research',patterns: [/\bweb\b/i,/\bresearch/i,/\bcompetitor/i,/\bmarket/i,/\bseo/i]},
  {capability: 'data_analysis',patterns: [/\bdata\b/i,/\banaly[sz]e/i,/\bforecast/i,/\bmetric/i,/\bkpi\b/i,/\bnumber/i]},
  {capability: 'writing',patterns: [/\bwrit/i,/\bcopy\b/i,/\bdraft/i,/\bemail/i,/\bmessage/i,/\bblog/i,/\bpost\b/i]},
  {capability: 'planning',patterns: [/\bplan/i,/\bstrateg/i,/\broadmap/i,/\bschedul/i,/\bprioriti/i]},
  {capability: 'communication',patterns: [/\breply\b/i,/\brespond\b/i,/\binbox\b/i,/\bcustomer\b/i,/\bclient/i,/\bsupport\b/i]},
  {capability: 'api_integration',patterns: [/\bapi\b/i,/\bendpoint/i,/\bwebhook/i,/\bintegration/i]}
];

function deriveCapabilities(text) {
  return CAPABILITY_RULES
    .filter((rule)=> rule.patterns.some((pattern)=> pattern.test(text)))
    .map((rule)=> rule.capability);
}

function deriveName(description) {
  const firstSentence=description.split(/[.!?\n]/)[0] || description;
  const words=firstSentence
    .replace(/^(i\s+(need|want)|we\s+(need|want)|create|build|make|add|a|an|the)\s+/gi,'')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0,3);

  if (!words.length) return 'Custom Specialist';

  return words
    .map((word)=> word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function deriveRole(description) {
  const lower=description.toLowerCase();

  for (const keyword of ['finance','marketing','operations','support','sales','content','procurement','hr ','legal','technical']) {
    if (lower.includes(keyword)) {
      return `${keyword.trim().replace(/^\w/,(char)=> char.toUpperCase())} Specialist`;
    }
  }

  return 'Custom business specialist';
}

function deriveSystemPrompt(description,business) {
  const context=business?.name ? ` for ${business.name}` : '';

  return [
    `You are a dedicated business specialist${context}.`,
    `Your mandate, as described by the owner: ${description.trim()}`,
    'Work within that mandate: ask when a request falls outside it, keep answers decision-ready, and never invent facts.'
  ].join(' ');
}

function deriveTier(description) {
  return description.length > 500 ? 'tier_deep' : 'tier_balanced';
}

/**
 * Stage 1 — draft only, writes nothing. Returns a manifest-shaped draft for
 * the review/edit gate (same shape as the JSON manifests under bots/).
 */
export function draftAgentManifest({description,files=[],urls=[],apiEndpoints=[]}) {
  const safeDescription=requireSafeContent(description);
  const capabilities=deriveCapabilities(safeDescription);
  const tools=[
    ...(files.length ? ['file_retrieval'] : []),
    ...(urls.length ? ['web_search'] : []),
    ...(apiEndpoints.length ? ['api_endpoint_call'] : [])
  ];

  return {
    schema_version: 1,
    slug: slugifyName(deriveName(safeDescription)),
    name: deriveName(safeDescription),
    role: deriveRole(safeDescription),
    system_prompt: deriveSystemPrompt(safeDescription,dataClient.peek().business),
    model_tier: deriveTier(safeDescription),
    agent_class: tools.length ? 'tooling_advanced' : 'chat_simple',
    tools,
    capabilities,
    knowledge_sources: {
      files,
      urls,
      apiEndpoints
    }
  };
}

/**
 * Stage 2 — install an approved (possibly edited) draft as a real bot +
 * knowledge sources + first agentFlow. The edit gate already ran in the UI;
 * re-validate the prompt here because the text is user-editable.
 */
export async function installAgentDraft(draft,{teamId=null}={}) {
  const state=dataClient.peek();
  const safePrompt=requireSafeContent(draft.system_prompt);

  const bot=await createBot({
    name: draft.name,
    role: draft.role,
    systemPrompt: safePrompt,
    modelTier: draft.model_tier || 'tier_balanced',
    teamId,
    isLead: false,
    consultableBots: [],
    agentClass: draft.agent_class || 'chat_simple',
    tools: [...(draft.tools || [])],
    capabilities: [...(draft.capabilities || [])],
    knowledgeScope: draft.knowledge_scope || ''
  });

  const sources=[
    ...(draft.knowledge_sources?.files || []).map((sourceRef)=> ({sourceType: 'file',sourceRef})),
    ...(draft.knowledge_sources?.urls || []).map((sourceRef)=> ({sourceType: 'web_url',sourceRef})),
    ...(draft.knowledge_sources?.apiEndpoints || []).map((sourceRef)=> ({sourceType: 'api_endpoint',sourceRef}))
  ];

  for (const source of sources) {
    await ingestKnowledgeSource({business: state.business?.id,bot: bot.id,...source});
  }

  const flow=await dataClient.create('agentFlows',{
    business: state.business?.id,
    bot: bot.id,
    name: `${bot.name} workflow`,
    steps: [
      {
        position: 1,
        stepType: 'ai',
        config: {instruction: safePrompt}
      }
    ]
  });

  return {bot,flow,sourcesInstalled: sources.length};
}

function teamRosterHeuristic(description) {
  const capabilities=deriveCapabilities(description);

  if (capabilities.length >= 3) {
    return capabilities.slice(0,3);
  }

  return ['analysis','execution','review'];
}

/**
 * Stage 1 (team) — draft a 2-3 specialist roster + Lead from a description.
 * Writes nothing; the review gate edits names/roles before install.
 */
export function draftTeamManifest({description,moduleName='headquarters'}) {
  const safeDescription=requireSafeContent(description);
  const specialisms=teamRosterHeuristic(safeDescription);
  const teamName=`${deriveName(safeDescription)} Team`;

  return {
    schema_version: 1,
    slug: slugifyName(teamName),
    name: teamName,
    module: moduleName,
    lead: {
      name: 'Lead',
      role: `${deriveName(safeDescription)} Lead`,
      system_prompt: `Lead the ${teamName}: consult your specialists, then synthesize one decision-ready answer. Context: ${safeDescription}`,
      model_tier: 'tier_deep'
    },
    specialists: specialisms.map((specialism,index)=> ({
      name: `${specialism.charAt(0).toUpperCase() + specialism.slice(1).replace(/_/g,' ')} Specialist ${index + 1}`,
      role: `${specialism.replace(/_/g,' ')} specialist`,
      system_prompt: `Focus on ${specialism.replace(/_/g,' ')} within the team's mandate: ${safeDescription}`,
      model_tier: 'tier_balanced'
    }))
  };
}

/**
 * Stage 2 (team) — install an approved team draft via the BB-1 plumbing.
 */
export async function installTeamDraft(draft) {
  return createCustomTeam({
    name: draft.name,
    module: draft.module || 'headquarters',
    leadName: draft.lead.name,
    leadRole: draft.lead.role,
    leadSystemPrompt: draft.lead.system_prompt,
    leadModelTier: draft.lead.model_tier,
    members: draft.specialists.map((specialist)=> ({
      name: specialist.name,
      role: specialist.role,
      systemPrompt: specialist.system_prompt,
      modelTier: specialist.model_tier
    }))
  });
}

/**
 * Legacy one-shot path (mocked): kept for compatibility with any caller that
 * wants draft+install without the review gate. New UI flows use
 * draftAgentManifest → installAgentDraft.
 */
export async function buildAgentFromDescription({
  business,
 description,
 files=[],
 urls=[],
 apiEndpoints=[]
}) {
  const draft=draftAgentManifest({description,files,urls,apiEndpoints});
  const {bot,flow}=await installAgentDraft(draft);
  return {bot,flow};
}

export const stripeWebhookHandler=async (payload,signature)=> ({
  accepted: Boolean(payload && signature),
  eventId: payload?.id || 'evt_mock',
  mocked: true
});

export async function ingestContactFormMessage(payload) {
  if (!payload?.email || !payload?.message) {
    throw new Error('Email and message are required.');
  }

  return dataClient.create('contactSubmissions',{
    ...payload,
    status: 'new',
    mocked: true
  });
}

export const handleStripeWebhook=stripeWebhookHandler;
export const ingestContactForm=ingestContactFormMessage;